import AdaptationTag from "@/components/display/AdaptationTag";
import { useAdaptedSummaryStatus } from "@/features/collection_home";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { CloudDownloadOutlined, FileAddOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Collapse, ConfigProvider, Modal } from "antd";
import { CollapseProps } from "antd/lib";
import { useTranslation } from "react-i18next";
import { PortfolioExportJobSchema } from "../types";
import { UseSummaryActionsResult } from "./useSummaryActions";
type SummaryDownloadModalProps = {
  schema: PortfolioExportJobSchema;
  summary: UseSummaryActionsResult;
  open: boolean;
  onClose: () => void;
};
export const SummaryDownloadModal = ({
  schema,
  summary,
  open,
  onClose
}: SummaryDownloadModalProps) => {
  const {
    t
  } = useTranslation("terms");
  const {
    status: adapted,
    download: downloadAdapted,
    isDownloading: isAdaptedDownloading
  } = useAdaptedSummaryStatus(schema, open);
  const {
    canRunAutoAdaptation
  } = useAdaptationPermissions({
    isLargeSite: schema.isLargeSite
  });
  const buttonsDisabled = !schema.assets.processedCount;
  const isSummaryProcessing = !!schema.summaryId && !schema.isSummaryGenerated;
  const isSummaryGenerated = !!schema.summaryId && schema.isSummaryGenerated;
  const renderBaseline = () => {
    if (isSummaryGenerated) {
      return <Button icon={<CloudDownloadOutlined />} onClick={() => summary.download(schema, schema.isLargeSite ? undefined : "baseline")} loading={summary.isDownloading} disabled={summary.isDownloading || buttonsDisabled}>
          Download
        </Button>;
    }
    if (isSummaryProcessing) {
      return <Button icon={<LoadingOutlined />} disabled>
          Generating...
        </Button>;
    }
    return <Button icon={<FileAddOutlined />} onClick={() => summary.generate(schema)} loading={summary.isGenerating} disabled={summary.isGenerating || buttonsDisabled} data-sentry-element="Button" data-sentry-component="renderBaseline" data-sentry-source-file="SummaryDownloadModal.tsx">
        Generate Summary
      </Button>;
  };
  const renderAdapted = () => {
    switch (adapted.kind) {
      case "ready":
        return <Button icon={<CloudDownloadOutlined />} onClick={() => downloadAdapted()} loading={isAdaptedDownloading} disabled={isAdaptedDownloading || buttonsDisabled}>
            Download
          </Button>;
      case "missing":
        return <Button icon={<FileAddOutlined />} onClick={() => adapted.generate()} loading={adapted.isGenerating} disabled={adapted.isGenerating || buttonsDisabled}>
            Generate {t("adaptationTypes.enhanced_resilience")} Summary
          </Button>;
      case "generating":
        return <Button icon={<LoadingOutlined />} disabled>
            Generating...
          </Button>;
      case "error":
        return <span className="text-red-500">Something went wrong. Please try again.</span>;
      case "loading":
      default:
        return <LoadingOutlined />;
    }
  };
  const items: CollapseProps["items"] = [{
    key: "risk",
    label: <div>
          <div className="inline-flex gap-2">
            Aggregated Summary Results <AdaptationTag type="baseline" />
          </div>
          <p className="mt-1 text-xs">
            Baseline Aggregated Summary output contains aggregated risk metrics and summary statistics for the
            portfolio, using asset input settings provided by the user at the time of upload. It provides a concise
            overview of portfolio-level climate risk across hazards and scenarios, making it suitable for reporting,
            benchmarking, and strategic decision-making.
          </p>
        </div>,
    children: renderBaseline()
  }, ...(canRunAutoAdaptation ? [{
    key: "enhanced_resilience",
    label: <div>
                <div className="inline-flex gap-2">
                  Auto Resilience Aggregated Summary Results <AdaptationTag type="enhanced_resilience" />
                </div>
                <p className="mt-1 text-xs">
                  Auto Resilience Aggregate Summary Results contains aggregated risk metrics and summary statistics for
                  the portfolio under automatically generated resilience configurations. It simulates how the
                  portfolio&apos;s physical climate risk outcomes are expected to change under enhanced resilience
                  assumptions, making it suitable for resilience planning, reporting, and strategic decision-making.
                </p>
              </div>,
    children: renderAdapted()
  }] : [])];
  return <Modal rootClassName="relative z-[1050]" width={640} title="Summary Results" open={open} onCancel={onClose} footer={null} destroyOnHidden data-sentry-element="Modal" data-sentry-component="SummaryDownloadModal" data-sentry-source-file="SummaryDownloadModal.tsx">
      <ConfigProvider theme={{
      components: {
        Collapse: {
          contentPadding: "8px inherit inherit inherit",
          colorTextDisabled: "var(--color-gray-400)"
        }
      }
    }} data-sentry-element="ConfigProvider" data-sentry-source-file="SummaryDownloadModal.tsx">
        <Collapse accordion className={`w-full items-center bg-surface`} size="small" expandIconPlacement="end" items={items} data-sentry-element="Collapse" data-sentry-source-file="SummaryDownloadModal.tsx" />
      </ConfigProvider>
    </Modal>;
};